// @givmo/fundraisers-module — one email of a fundraiser's series (EMC-WEB1; Email Campaign plan §1.1 step 3),
// built to mockups 15 (Audience) and 17 (Test and schedule) of the walkthrough Jonathan approved. ⚠ Those
// two frames await his notes (screens 15 onward); they are built as drawn.
//
// The page is the email as a recipient gets it (the server's own render, `message-email-frame.tsx`) beside
// a panel of three tabs: Design opens the designer (EMC-WEB10's page), Audience picks who gets it, and
// Schedule sends a test and sets when it goes out.
//
// ⚠ EVERY FACT IS THE SERVER'S. "About N people" is its count; whether the email can be scheduled is
// the sender read's `can_schedule`; a refusal (the time, the audience, the body, the freeze) is its own
// sentence, shown in the panel where the person can act on it. The page checks only the obvious time
// bounds (15 minutes ahead, a year away) before the server checks them again under the email's lock.
// The segments list is the CRM's own picker, rented (D-EMC-1): names and counts only (D-EMC-10).

import { useMemo, useState, type ReactNode } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, CircleCheck, Copy, Trash2 } from 'lucide-react';
import { isGivmoError } from '@givmo/api-client';
import { CrmSegmentPicker } from '@givmo/crm';
import {
  Button,
  Card,
  Checkbox,
  DatePicker,
  EmptyState,
  Icon,
  RadioTile,
  RadioTileGroup,
  Select,
  Tabs,
  testIdProp,
  toast,
} from '@givmo/design-system';
import { ConfirmDialog } from './confirm-dialog';
import { EMAILS_COPY as C } from './emails-copy';
import {
  EMPTY_AUDIENCE,
  QUARTER_HOURS,
  audienceBody,
  audienceIsEmpty,
  clockLabel,
  dateFromField,
  defaultZone,
  emailDay,
  emailEditable,
  emailRefusal,
  emailStandingNote,
  emailStatus,
  emailTime,
  fieldFromDate,
  isEmailsUnavailable,
  liveAudience,
  namesEveryone,
  scheduleProblem,
  usableZone,
  withEveryone,
  zoneChoices,
  zoneLabel,
  zonedFields,
  zonedInstant,
  type EmailAudience,
  type EmailRow,
} from './emails-decode';
import {
  audienceEstimateOptions,
  audienceOptionsOptions,
  emailKeys,
  emailOptions,
  emailPreviewOptions,
  emailsSeriesOptions,
} from './emails-queries';
import { cancelEmail, scheduleEmail, sendTestEmail, updateEmail } from './emails-transport';
import { zoneName } from './layers-decode';
import {
  mayManageCampaignEmails,
  mayWriteFundraisers,
  orgSettingsHref,
} from './fundraisers-config';
import { useFundraisersModule } from './fundraisers-context';
import { fundraiserOptions } from './fundraisers-queries';
import { FUNDRAISERS_ROUTES, type EmailStep } from './fundraisers-routes';
import {
  FundraiserNotHere,
  FundraisersLoading,
  FundraisersPageShell,
  FundraisersReadState,
  ModuleLink,
} from './fundraisers-state';
import { EmailsUnavailable } from './emails-tab';
import { EmailResultsCard } from './email-results';
import { EmailPreviewStage } from './email-preview-stage';
import { emailDeletable, useDeleteEmail } from './email-delete';
import { useDuplicateEmail } from './email-duplicate';
import { useSenderIdentity } from './sender-identity';
import { useLivePage } from './use-live-page';

/** The picker's ids for the two audiences the CRM's segment list doesn't hold. */
const EVERYONE_ROW = 'everyone';
const CAMPAIGN_DONORS_ROW = 'campaign-donors';

export function EmailPage({
  fundraiserId,
  emailId,
  step = 'audience',
}: {
  fundraiserId: string;
  emailId: string;
  step?: EmailStep;
}) {
  const config = useFundraisersModule();
  const orgRef = config.orgRef ?? '';
  const enabled = config.orgRef !== null;
  const email = useQuery({ ...emailOptions(orgRef, fundraiserId, emailId), enabled });
  const fundraiser = useQuery({ ...fundraiserOptions(orgRef, fundraiserId), enabled });
  const series = useQuery({ ...emailsSeriesOptions(orgRef, fundraiserId), enabled });
  // Held above the form's key: a refused schedule saves the time first, and the re-read that shows
  // it starts the form again, which must not take the server's sentence with it.
  const [refusal, setRefusal] = useState<{ emailId: string; message: string } | null>(null);

  const back = FUNDRAISERS_ROUTES.emails(fundraiserId);
  // A miss on the email whose fundraiser this organization doesn't hold either is a link from another of
  // the person's organizations: wait for the fundraiser's own answer, then say where it is.
  const fundraiserMissing = isGivmoError(fundraiser.error) && fundraiser.error.status === 404;
  if (email.isError && isEmailsUnavailable(email.error) && fundraiser.isPending) {
    return (
      <FundraisersPageShell testId="page-fundraiser-email">
        <FundraisersLoading />
      </FundraisersPageShell>
    );
  }
  if (email.isError) {
    return (
      <FundraisersPageShell testId="page-fundraiser-email">
        {fundraiserMissing ? (
          <FundraiserNotHere fundraiserId={fundraiserId} />
        ) : isEmailsUnavailable(email.error) ? (
          <EmailsUnavailable />
        ) : (
          <FundraisersReadState error={email.error} onRetry={() => void email.refetch()} />
        )}
      </FundraisersPageShell>
    );
  }
  if (email.isPending || series.isPending) {
    return (
      <FundraisersPageShell testId="page-fundraiser-email">
        <FundraisersLoading />
      </FundraisersPageShell>
    );
  }
  const row = email.data;
  const index = series.data?.findIndex((item) => item.id === row.id) ?? -1;
  return (
    <LoadedEmail
      // A new answer for the email starts its form from what the server holds.
      key={`${row.id}:${row.state}:${row.scheduledFor ?? ''}:${row.tz ?? ''}`}
      fundraiserId={fundraiserId}
      row={row}
      step={step}
      backHref={back}
      backLabel={fundraiser.data?.name ?? C.tab}
      position={index >= 0 && series.data ? C.position(index + 1, series.data.length) : null}
      seriesZone={defaultZone(series.data, config.orgTimeZone)}
      refusal={refusal?.emailId === row.id ? refusal.message : null}
      setRefusal={(message) => setRefusal(message === null ? null : { emailId: row.id, message })}
    />
  );
}

function LoadedEmail({
  fundraiserId,
  row,
  step,
  backHref,
  backLabel,
  position,
  seriesZone,
  refusal,
  setRefusal,
}: {
  fundraiserId: string;
  row: EmailRow;
  step: EmailStep;
  backHref: string;
  backLabel: string;
  position: string | null;
  /** Where an email with no zone yet starts: its series' last zone, else the reader's. */
  seriesZone: string;
  refusal: string | null;
  setRefusal: (message: string | null) => void;
}) {
  const config = useFundraisersModule();
  const orgRef = config.orgRef ?? '';
  const client = useQueryClient();
  const live = useLivePage();
  const writable = mayWriteFundraisers(config);
  const manages = mayManageCampaignEmails(config);
  const duplicate = useDuplicateEmail(fundraiserId);
  const remove = useDeleteEmail(fundraiserId, {
    onDeleted: () => config.onNavigate(FUNDRAISERS_ROUTES.emails(fundraiserId)),
  });
  const sender = useSenderIdentity(config.orgRef);
  const canScheduleNow = sender.data?.canSchedule === true;
  const orgZone = config.orgTimeZone && usableZone(config.orgTimeZone) ? config.orgTimeZone : null;
  const settingsHref = orgSettingsHref(config);
  // A test goes out only from a domain the server can send as (review-polish A-1): until then Send
  // test waits and says why, rather than meeting the server's refusal.
  const testWaits =
    sender.data != null && !canScheduleNow ? C.testWaitsOnDomain(sender.data.domain) : null;

  const scheduled = row.state === 'scheduled';
  const draftLike = row.state === 'draft' || row.state === 'cancelled';
  const editable = emailEditable(row) && (scheduled ? manages : writable);
  const goneOut = !draftLike && !scheduled;

  // ── What the person is choosing ──────────────────────────────────────────────────────────────
  const [audience, setAudience] = useState<EmailAudience>(row.audience ?? EMPTY_AUDIENCE);
  const [rule, setRule] = useState(row.rule);
  const initialZone = row.tz ?? seriesZone;
  const initialFields =
    row.scheduledFor === null ? null : zonedFields(row.scheduledFor, initialZone);
  const [zone, setZone] = useState(initialZone);
  const [date, setDate] = useState(initialFields?.date ?? '');
  const [time, setTime] = useState(initialFields?.time ?? '09:00');
  const [testedAt, setTestedAt] = useState<string | null>(null);
  const [cancelOpen, setCancelOpen] = useState(false);

  const audienceDirty =
    JSON.stringify(audienceBody(audience)) !==
      JSON.stringify(audienceBody(row.audience ?? EMPTY_AUDIENCE)) || rule !== row.rule;
  const instant = date === '' ? null : zonedInstant({ date, time }, zone);
  const timeDirty =
    (instant === null ? null : Date.parse(instant)) !==
      (row.scheduledFor === null ? null : Date.parse(row.scheduledFor)) ||
    zone !== (row.tz ?? initialZone);

  const options = useQuery({ ...audienceOptionsOptions(orgRef, fundraiserId), enabled: true });
  const estimate = useQuery({
    ...audienceEstimateOptions(orgRef, fundraiserId, audience),
    enabled: !audienceIsEmpty(audience),
    placeholderData: keepPreviousData,
  });
  const everyoneCount = useQuery({
    ...audienceEstimateOptions(orgRef, fundraiserId, withEveryone(EMPTY_AUDIENCE, true)),
    enabled: options.data?.crmAvailable === true,
  });
  const preview = useQuery({ ...emailPreviewOptions(orgRef, fundraiserId, row.id) });
  // What a save writes: the audience without a segment the picker can't show (deleted since it was saved),
  // which nothing on the screen could otherwise take out and the server refuses (review-24 M4).
  const writtenAudience = () =>
    audienceBody(liveAudience(audience, options.data, estimate.data?.missingSegmentIds));

  const refresh = async () => {
    await Promise.all([
      client.invalidateQueries({ queryKey: emailKeys.email(orgRef, fundraiserId, row.id) }),
      client.invalidateQueries({ queryKey: emailKeys.series(orgRef, fundraiserId) }),
    ]);
  };
  const refused = (error: unknown) => {
    if (!live()) return;
    const answer = emailRefusal(error);
    if (answer.kind === 'refused') setRefusal(answer.message);
    else if (answer.kind === 'gone') void refresh();
    else toast.error(C.failed);
  };
  const go = (next: EmailStep) =>
    config.onNavigate(FUNDRAISERS_ROUTES.email(fundraiserId, row.id, next));

  const saveAudience = useMutation({
    mutationFn: () =>
      updateEmail(orgRef, fundraiserId, row.id, {
        audience_spec: writtenAudience(),
        audience_rule: rule,
      }),
    onMutate: () => setRefusal(null),
    onSuccess: async () => {
      await refresh();
      if (live()) go('schedule');
    },
    onError: refused,
  });

  const schedule = useMutation({
    mutationFn: async () => {
      if (instant === null) throw new Error('unreadable time');
      if (scheduled) {
        // Only what changed: an unchanged time is not checked again (review-24 L1), and a scheduled
        // email's new audience is committed here, by Update schedule, never by Continue (L2).
        return updateEmail(orgRef, fundraiserId, row.id, {
          ...(timeDirty ? { scheduled_for: instant, tz: zone } : {}),
          ...(audienceDirty ? { audience_spec: writtenAudience(), audience_rule: rule } : {}),
        });
      }
      // A draft keeps what was chosen before it is scheduled, so a refused schedule still leaves
      // its day and time on it (the KCC dev build: a refused draft came back with no date). A
      // draft's time is not checked on a save; the schedule below checks it.
      if (timeDirty || audienceDirty)
        await updateEmail(orgRef, fundraiserId, row.id, {
          ...(timeDirty ? { scheduled_for: instant, tz: zone } : {}),
          ...(audienceDirty ? { audience_spec: writtenAudience(), audience_rule: rule } : {}),
        });
      return scheduleEmail(orgRef, fundraiserId, row.id, { scheduled_for: instant, tz: zone });
    },
    onMutate: () => setRefusal(null),
    onSuccess: async () => {
      if (live() && instant !== null)
        toast.success(
          C.scheduled(
            C.summaryWhen(
              emailDay(instant, zone),
              emailTime(instant, zone),
              zoneLabel(zone, new Date(instant)),
            ),
          ),
        );
      await refresh();
    },
    onError: (error) => {
      refused(error);
      // What a refused schedule saved first is the email's now.
      void refresh();
    },
  });

  // A draft keeps its date and time (and any new audience) as a plan, with no domain needed: the server
  // holds a draft's time until it is scheduled. Schedule stays the one act that sends.
  const saveDate = useMutation({
    mutationFn: () => {
      if (instant === null) throw new Error('unreadable time');
      return updateEmail(orgRef, fundraiserId, row.id, {
        scheduled_for: instant,
        tz: zone,
        ...(audienceDirty ? { audience_spec: writtenAudience(), audience_rule: rule } : {}),
      });
    },
    onMutate: () => setRefusal(null),
    onSuccess: async () => {
      if (live()) toast.success(C.dateSaved);
      await refresh();
    },
    onError: refused,
  });

  const cancel = useMutation({
    mutationFn: () => cancelEmail(orgRef, fundraiserId, row.id),
    onMutate: () => setRefusal(null),
    onSuccess: async () => {
      if (live()) {
        setCancelOpen(false);
        toast.success(C.cancelled);
      }
      await refresh();
    },
    onError: (error) => {
      setCancelOpen(false);
      refused(error);
    },
  });

  const test = useMutation({
    mutationFn: () => sendTestEmail(orgRef, fundraiserId, row.id),
    onMutate: () => setRefusal(null),
    onSuccess: () => {
      if (!live()) return;
      setTestedAt(
        new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit' }).format(new Date()),
      );
    },
    onError: refused,
  });

  const busy =
    saveAudience.isPending || schedule.isPending || saveDate.isPending || cancel.isPending;
  // A scheduled email's unchanged time stands as the server accepted it; only a new time is checked (L1).
  const problem = scheduled && !timeDirty ? null : date === '' ? 'pick' : scheduleProblem(instant);
  const status = C.status[emailStatus(row)];
  // A held, late or failed email says why, never "goes out in a few minutes" (the KCC dev build).
  const standing = emailStandingNote(row, C);
  // The header says where the email stands and when it goes out, as the designer's does.
  const when =
    row.scheduledFor === null
      ? C.noTime
      : C.headerWhen(emailDay(row.scheduledFor, row.tz), emailTime(row.scheduledFor, row.tz));
  const meta = [
    position,
    status,
    when,
    // An email that went out is the record: "Saved" would read as a draft (the user test).
    goneOut ? null : busy ? C.saving : audienceDirty || timeDirty ? null : C.saved,
  ]
    .filter((part): part is string => part !== null)
    .join(' · ');

  // ── The audience list: the CRM's segments, then the two audiences it doesn't hold ─────────────
  const pickerRows = useMemo(
    () => [
      ...(options.data?.segments ?? []),
      ...(options.data?.crmAvailable === true
        ? [{ id: EVERYONE_ROW, name: C.everyone, count: everyoneCount.data?.aboutPeople ?? null }]
        : []),
      {
        id: CAMPAIGN_DONORS_ROW,
        name: C.campaignDonors,
        count: options.data?.campaignDonors ?? null,
      },
    ],
    [options.data, everyoneCount.data],
  );
  const pickerSelected = [
    ...audience.segmentIds,
    ...(namesEveryone(audience) ? [EVERYONE_ROW] : []),
    ...(audience.campaignDonors ? [CAMPAIGN_DONORS_ROW] : []),
  ];
  const pick = (ids: string[]) => {
    const chosen = new Set(ids);
    setAudience((current) => ({
      ...withEveryone(current, chosen.has(EVERYONE_ROW)),
      segmentIds: ids.filter((id) => id !== EVERYONE_ROW && id !== CAMPAIGN_DONORS_ROW),
      campaignDonors: chosen.has(CAMPAIGN_DONORS_ROW),
    }));
  };
  const toggleTag = (label: string, on: boolean) =>
    setAudience((current) => ({
      ...current,
      tags: on ? [...current.tags, label] : current.tags.filter((tag) => tag !== label),
    }));

  const about = audienceIsEmpty(audience) ? null : (estimate.data?.aboutPeople ?? null);
  // "Counting…" only while a count is on its way; an answer with no count, or a failed read, says so (M2).
  const countLine =
    about !== null
      ? C.about(about)
      : estimate.isError
        ? C.countFailed
        : estimate.isFetching || estimate.data === undefined
          ? C.counting
          : (estimate.data.message ?? C.notCounted);
  const picks =
    audience.segmentIds.length +
    audience.tags.length +
    (namesEveryone(audience) ? 1 : 0) +
    (audience.campaignDonors ? 1 : 0);
  const summaryZone =
    instant === null ? '' : zoneLabel(zone, new Date(instant)).replace(/ \(.*\)$/, '');

  // ── Panels ─────────────────────────────────────────────────────────────────────────────────────
  const audiencePanel = (
    <fieldset
      disabled={!editable || busy}
      className="flex min-w-0 flex-col gap-24"
      {...testIdProp('fundraiser-email-audience')}
    >
      {row.audience === null && <Note>{C.audienceUnreadable}</Note>}
      {(estimate.data?.missingSegmentIds.length ?? 0) > 0 && <Note>{C.segmentGone}</Note>}
      {(estimate.data?.unreadableSegmentIds.length ?? 0) > 0 && <Note>{C.segmentUnreadable}</Note>}
      {options.data?.crmAvailable === false && <Note>{options.data.message ?? C.noCrm}</Note>}
      <p
        className="text-s13 leading-h15 text-text-secondary"
        {...testIdProp('fundraiser-email-audience-rule')}
      >
        {C.audienceRule}
      </p>
      <CrmSegmentPicker
        rows={pickerRows}
        selected={pickerSelected}
        onChange={pick}
        contactsVisible={config.crmAccess?.contactsVisible ?? true}
        onRequestAccess={config.crmAccess?.onRequestAccess}
        accessRequestPending={config.crmAccess?.accessRequestPending}
        title={C.segments}
        note={C.segmentsNote}
        formatCount={C.countToday}
        testIdPrefix="fundraiser-email-segments"
      />
      {(options.data?.tags.length ?? 0) > 0 && (
        <section className="flex flex-col gap-12" {...testIdProp('fundraiser-email-tags')}>
          <h3 className="font-sans text-s13 font-w600 tracking-givmo text-text-strong">{C.tags}</h3>
          <div className="flex flex-col">
            {options.data!.tags.map((tag) => (
              <label key={tag.label} className="flex items-center gap-10 py-6 font-sans text-s14">
                <Checkbox
                  appearance="saas"
                  size={18}
                  checked={audience.tags.includes(tag.label)}
                  onCheckedChange={(checked) => toggleTag(tag.label, checked === true)}
                />
                <span className="min-w-0 flex-1 truncate text-text-strong">{tag.label}</span>
                <span className="tabular-nums text-text-tertiary">{C.countToday(tag.count)}</span>
              </label>
            ))}
          </div>
        </section>
      )}
      <section className="flex flex-col gap-12">
        <h3 className="font-sans text-s13 font-w600 tracking-givmo text-text-strong">
          {C.thisEmailIs}
        </h3>
        <RadioTileGroup
          value={rule}
          onValueChange={(value) => setRule(value === 'thank' ? 'thank' : 'ask')}
          name="fundraiser-email-rule"
          disabled={!editable || busy}
        >
          <RadioTile value="ask" label={C.ask} description={C.askLine} />
          <RadioTile value="thank" label={C.thank} description={C.thankLine} />
        </RadioTileGroup>
      </section>
      <SummaryBox testId="fundraiser-email-about">
        {audienceIsEmpty(audience) ? (
          <p className="text-s14 text-text-secondary">{C.pickSomeone}</p>
        ) : (
          <>
            <p className="text-s14 font-w600 leading-h15 text-text-strong">{countLine}</p>
            {about === 0 && !goneOut && (
              <p className="text-s13 text-text-secondary">{C.segmentsNote}</p>
            )}
            {estimate.isError && !estimate.isFetching && (
              <button
                type="button"
                className="self-start text-s13 font-w600 text-brand-600 hover:underline"
                onClick={() => void estimate.refetch()}
                {...testIdProp('fundraiser-email-about-retry')}
              >
                {C.retry}
              </button>
            )}
            {picks > 1 && <p className="text-s13 text-text-secondary">{C.anyOf}</p>}
          </>
        )}
      </SummaryBox>
    </fieldset>
  );

  const zoneOptions = zoneChoices(zone).map((value) => ({ value, label: zoneLabel(value) }));
  const schedulePanel = (
    <div className="flex min-w-0 flex-col gap-20" {...testIdProp('fundraiser-email-schedule')}>
      <section className="flex flex-col gap-10">
        <h3 className="font-sans text-s14 font-w600 tracking-givmo text-text-strong">
          {C.sendATest}
        </h3>
        <div className="flex items-center gap-10">
          <p
            className="min-w-0 flex-1 text-s14 text-text-secondary"
            {...testIdProp('fundraiser-email-test-to')}
          >
            {config.viewerEmail ? (
              <>
                {C.testGoesTo}{' '}
                <span className="font-w600 text-text-strong wrap-anywhere">
                  {config.viewerEmail}
                </span>
              </>
            ) : (
              C.testTo
            )}
          </p>
          <Button
            appearance="saas"
            tone="secondary"
            testId="fundraiser-email-test"
            loading={test.isPending}
            disabled={
              !writable || goneOut || test.isPending || sender.isPending || testWaits !== null
            }
            aria-describedby={testWaits !== null ? 'fundraiser-email-test-waits' : undefined}
            onClick={() => test.mutate()}
          >
            {C.sendTest}
          </Button>
        </div>
        {testWaits !== null && !goneOut && (
          <p
            id="fundraiser-email-test-waits"
            className="text-s13 text-text-secondary"
            {...testIdProp('fundraiser-email-test-waits')}
          >
            {testWaits}
          </p>
        )}
        {testedAt !== null && (
          <p
            role="status"
            className="flex items-center gap-6 text-s13 font-w600 text-success-700"
            {...testIdProp('fundraiser-email-test-done')}
          >
            <CircleCheck aria-hidden className="size-14 shrink-0" />
            {C.testDone(testedAt)}
          </p>
        )}
      </section>
      <hr className="border-border-subtle" />
      {goneOut && row.state !== 'failed' && row.scheduledFor !== null ? (
        // The user test: an email that went out shows when it went and to how many, read-only, with
        // the server's own count (never "about"), and no date or time to change. A failed email
        // never went out, so it keeps the fields below, disabled.
        <SummaryBox testId="fundraiser-email-went-out">
          <h3 className="font-sans text-s14 font-w600 tracking-givmo text-text-strong">
            {C.wentOut}
          </h3>
          <p className="text-s14 font-w600 leading-h15 text-text-strong">
            {C.summaryWhen(
              emailDay(row.scheduledFor, row.tz),
              emailTime(row.scheduledFor, row.tz),
              zoneLabel(row.tz ?? zone, new Date(row.scheduledFor)).replace(/ \(.*\)$/, ''),
            )}
          </p>
          {(row.noRecipients || row.results !== null) && (
            <p className="text-s13 text-text-secondary">
              {C.wentTo(
                row.noRecipients ? 0 : (row.results?.emailed ?? 0),
                sender.data?.fromAddress ?? null,
              )}
            </p>
          )}
        </SummaryBox>
      ) : (
        <>
          <fieldset
            disabled={!editable || busy}
            className="flex min-w-0 flex-col gap-14"
            {...testIdProp('fundraiser-email-when')}
          >
            <h3 className="font-sans text-s14 font-w600 tracking-givmo text-text-strong">
              {C.whenItSends}
            </h3>
            <DatePicker
              appearance="saas"
              label={C.date}
              value={dateFromField(date)}
              onValueChange={(value) => setDate(value === undefined ? '' : fieldFromDate(value))}
              formatDate={(value) =>
                new Intl.DateTimeFormat('en-US', {
                  weekday: 'short',
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                }).format(value)
              }
              placeholder={C.pickTime}
              disabled={!editable || busy}
              testId="fundraiser-email-date"
            />
            <div className="grid grid-cols-[minmax(0,2fr)_minmax(0,3fr)] gap-10">
              <Select
                appearance="saas"
                label={C.time}
                value={time}
                onValueChange={(value) => value !== null && setTime(value)}
                options={QUARTER_HOURS.map((value) => ({ value, label: clockLabel(value) }))}
                disabled={!editable || busy}
                testId="fundraiser-email-time"
              />
              <Select
                appearance="saas"
                label={C.timeZone}
                value={zone}
                onValueChange={(value) => value !== null && setZone(value)}
                options={zoneOptions}
                disabled={!editable || busy}
                testId="fundraiser-email-zone"
              />
            </div>
            {orgZone !== null && (
              <p
                className="text-s12 leading-h15 text-text-secondary"
                {...testIdProp('fundraiser-email-org-zone')}
              >
                {/* Named as Settings and the Campaign tab name it ("Eastern Time"). */}
                {C.orgZone(zoneName(orgZone))}
                {settingsHref !== null && (
                  <>
                    {' '}
                    <ModuleLink
                      href={settingsHref}
                      className="font-w600 text-brand-700 hover:underline"
                      testId="fundraiser-email-zone-settings"
                    >
                      {C.changeZone}
                    </ModuleLink>
                  </>
                )}
              </p>
            )}
          </fieldset>
          {instant !== null && (
            <SummaryBox testId="fundraiser-email-when-summary">
              <p className="text-s14 font-w600 leading-h15 text-text-strong">
                {C.summaryWhen(emailDay(instant, zone), emailTime(instant, zone), summaryZone)}
              </p>
              <p className="text-s13 text-text-secondary">
                {C.summaryTo(about, sender.data?.fromAddress ?? null)}
              </p>
              {about === 0 && !goneOut && (
                <p className="text-s13 text-text-secondary">{C.segmentsNote}</p>
              )}
              {rule === 'ask' && about !== null && (
                <p className="text-s13 text-text-secondary">{C.summaryAskSkip}</p>
              )}
            </SummaryBox>
          )}
        </>
      )}
      {editable && (problem === 'too-soon' || problem === 'too-far') && (
        <p className="text-s13 text-danger-700" {...testIdProp('fundraiser-email-time-problem')}>
          {problem === 'too-soon' ? C.tooSoon : C.tooFar}
        </p>
      )}
      {/* Beside a draft's greyed Schedule the hint says why it waits; elsewhere this note does. */}
      {sender.data != null &&
        !canScheduleNow &&
        !goneOut &&
        !(draftLike && editable && manages) && <Note>{C.domainNotReady}</Note>}
      {!manages && !goneOut && <Note>{C.ownersSchedule}</Note>}
    </div>
  );

  const unchanged = scheduled && !timeDirty && !audienceDirty;
  const scheduleDisabled =
    !manages || !canScheduleNow || !editable || problem !== null || busy || unchanged;
  // Why the act is greyed, beside it: the notes above say the rest (who may, the domain, the time, the
  // freeze, when nothing can change).
  // Before the domain is ready a draft can still keep its date (Save date); Schedule says why it waits.
  const domainWaits = sender.data != null && !canScheduleNow;
  const scheduleHint =
    !scheduleDisabled || busy || goneOut || !manages || !editable
      ? null
      : domainWaits && draftLike
        ? C.scheduleWaitsOnDomain(sender.data?.domain ?? sender.data?.suggestedDomain ?? null)
        : unchanged
          ? C.nothingChanged
          : problem === 'pick'
            ? C.pickTime
            : null;
  // Save date: a draft's plan, offered on every draft so a picked date is never lost, for a readable
  // time that changed. Schedule stays the one act that sends.
  const offerSaveDate = draftLike && editable;
  const saveDateDisabled = busy || !timeDirty || instant === null || problem !== null;

  return (
    <FundraisersPageShell testId="page-fundraiser-email">
      <header className="flex min-w-0 flex-wrap items-baseline gap-x-14 gap-y-4">
        <a
          href={backHref}
          className="inline-flex items-center gap-8 text-s14 font-w600 text-text-strong hover:underline"
          onClick={(event) => {
            event.preventDefault();
            config.onNavigate(backHref);
          }}
          {...testIdProp('fundraiser-email-back')}
        >
          <ArrowLeft aria-hidden className="size-16" />
          {backLabel}
        </a>
        <span aria-hidden className="text-s18 text-text-tertiary">
          /
        </span>
        <h1 className="min-w-0 font-sans text-s20 font-w600 tracking-givmo text-text-strong wrap-anywhere">
          {row.untouchedCopy && (
            <span
              className="font-w400 text-text-tertiary"
              {...testIdProp('fundraiser-email-copy-of')}
            >
              {C.copyOf}{' '}
            </span>
          )}
          {row.subject || C.noSubject}
        </h1>
        <p className="text-s13 text-text-secondary" {...testIdProp('fundraiser-email-meta')}>
          {meta}
        </p>
        {writable && (
          <span className="ml-auto flex items-center gap-8 self-center">
            <Button
              appearance="saas"
              tone="secondary"
              size="sm"
              leadingIcon={<Icon icon={Copy} size={14} aria-hidden />}
              loading={duplicate.isPending}
              disabled={remove.isPending}
              onClick={() => duplicate.start(row.id)}
              testId="fundraiser-email-duplicate"
            >
              {C.duplicate}
            </Button>
            {/* Only an email not scheduled: a scheduled one is cancelled first, and one that went out is
                the record. */}
            {emailDeletable(row) && (
              <Button
                appearance="saas"
                tone="secondary"
                size="sm"
                className="text-danger-700"
                leadingIcon={<Icon icon={Trash2} size={14} aria-hidden />}
                loading={remove.isPending}
                disabled={busy || duplicate.isPending}
                onClick={() => remove.start(row)}
                testId="fundraiser-email-delete"
              >
                {C.delete}
              </Button>
            )}
          </span>
        )}
        {duplicate.dialog}
        {remove.dialog}
      </header>
      <div className="grid min-w-0 grid-cols-1 items-start gap-24 lg:grid-cols-[minmax(0,1fr)_360px]">
        <Card
          flush
          testId="fundraiser-email-preview"
          className="flex min-h-[560px] min-w-0 flex-col overflow-hidden lg:sticky lg:top-24 lg:h-[calc(100dvh-196px)]"
        >
          {preview.isPending ? (
            <p role="status" className="p-24 text-s14 text-text-secondary">
              {C.previewLoading}
            </p>
          ) : preview.data?.html ? (
            // The designer's own preview (`email-preview-stage.tsx`): the same render, the same frame,
            // the same "First name" chip and footer mark (the polish-1 user test).
            <div className="flex min-h-0 flex-1 flex-col bg-neutral-50 px-24 py-18">
              <EmailPreviewStage
                html={preview.data.html}
                subject={row.subject}
                width="desktop"
                empty={false}
                orgName={config.orgName}
                testIdPrefix="fundraiser-email-preview"
                title={C.previewTitle(row.subject || C.noSubject)}
              />
            </div>
          ) : (
            <EmptyState
              testId="fundraiser-email-preview-empty"
              title={C.previewEmpty}
              action={
                draftLike && writable ? (
                  <Button
                    appearance="saas"
                    tone="secondary"
                    testId="fundraiser-email-open-designer"
                    onClick={() =>
                      config.onNavigate(FUNDRAISERS_ROUTES.emailDesign(fundraiserId, row.id))
                    }
                  >
                    {C.openDesigner}
                  </Button>
                ) : undefined
              }
            />
          )}
        </Card>
        <div className="flex min-w-0 flex-col gap-16">
          {/* EMC-WEB2: once it went out, what it did — the figures its row shows on the Emails tab. */}
          <EmailResultsCard row={row} />
          <Card flush testId="fundraiser-email-panel" className="flex min-w-0 flex-col">
            <Tabs.Root
              appearance="saas"
              value={step}
              onValueChange={(value) =>
                value === 'design'
                  ? config.onNavigate(FUNDRAISERS_ROUTES.emailDesign(fundraiserId, row.id))
                  : go(value === 'schedule' ? 'schedule' : 'audience')
              }
              className="flex min-w-0 flex-1 flex-col"
            >
              <Tabs.List aria-label={C.tab} className="px-20 pt-8">
                <Tabs.Tab value="design" testId="fundraiser-email-design-tab" disabled={goneOut}>
                  {C.design}
                </Tabs.Tab>
                <Tabs.Tab value="audience" testId="fundraiser-email-audience-tab">
                  {C.audience}
                </Tabs.Tab>
                <Tabs.Tab value="schedule" testId="fundraiser-email-schedule-tab">
                  {C.schedule}
                </Tabs.Tab>
              </Tabs.List>
              <div className="flex min-w-0 flex-1 flex-col gap-16 px-20 py-20">
                {goneOut && (
                  <Note>
                    {row.state === 'failed'
                      ? C.failedNote
                      : row.everyoneHadGiven
                        ? C.everyoneGaveNote
                        : row.noRecipients
                          ? C.noOneNote
                          : C.locked}
                  </Note>
                )}
                {standing !== null ? (
                  <Note testId="fundraiser-email-standing">{standing}</Note>
                ) : (
                  row.frozen && scheduled && <Note>{C.frozen}</Note>
                )}
                <Tabs.Panel value="audience" focusable={false} className="pt-0">
                  {audiencePanel}
                </Tabs.Panel>
                <Tabs.Panel value="schedule" focusable={false} className="pt-0">
                  {schedulePanel}
                </Tabs.Panel>
                {refusal !== null && (
                  <p
                    role="alert"
                    className="text-s13 text-danger-700"
                    {...testIdProp('fundraiser-email-refusal')}
                  >
                    {refusal}
                  </p>
                )}
              </div>
              {/* An email that went out has nothing to schedule: no empty bar under its record. */}
              {(step === 'audience' || !goneOut) && (
                <div className="flex flex-wrap items-center justify-end gap-10 border-t border-border-subtle px-20 py-16">
                  {step === 'audience' ? (
                    <Button
                      appearance="saas"
                      testId="fundraiser-email-continue"
                      loading={saveAudience.isPending}
                      disabled={busy || (editable && audienceIsEmpty(audience))}
                      onClick={() =>
                        // A scheduled email's audience is committed by Update schedule (L2).
                        editable && audienceDirty && !scheduled
                          ? saveAudience.mutate()
                          : go('schedule')
                      }
                    >
                      {C.continue}
                    </Button>
                  ) : (
                    <>
                      {scheduleHint !== null && (
                        <p
                          id="fundraiser-email-schedule-hint"
                          className="mr-auto text-s13 text-text-secondary"
                          {...testIdProp('fundraiser-email-schedule-hint')}
                        >
                          {scheduleHint}
                        </p>
                      )}
                      {scheduled && manages && (
                        <Button
                          appearance="saas"
                          tone="secondary"
                          testId="fundraiser-email-cancel"
                          disabled={busy}
                          onClick={() => setCancelOpen(true)}
                        >
                          {C.cancelAct}
                        </Button>
                      )}
                      {offerSaveDate && (
                        <Button
                          appearance="saas"
                          tone="secondary"
                          testId="fundraiser-email-save-date"
                          loading={saveDate.isPending}
                          disabled={saveDateDisabled}
                          onClick={() => saveDate.mutate()}
                        >
                          {C.saveDate}
                        </Button>
                      )}
                      {!goneOut && (
                        <Button
                          appearance="saas"
                          testId="fundraiser-email-schedule-act"
                          loading={schedule.isPending}
                          disabled={scheduleDisabled}
                          aria-describedby={
                            scheduleHint !== null ? 'fundraiser-email-schedule-hint' : undefined
                          }
                          onClick={() => schedule.mutate()}
                        >
                          {scheduled ? C.updateSchedule : C.scheduleAct}
                        </Button>
                      )}
                    </>
                  )}
                </div>
              )}
            </Tabs.Root>
          </Card>
        </div>
      </div>
      <ConfirmDialog
        open={cancelOpen}
        onOpenChange={setCancelOpen}
        title={C.cancelTitle}
        body={C.cancelBody(row.scheduledFor === null ? null : when)}
        confirmLabel={C.cancelConfirm}
        cancelLabel={C.cancelKeep}
        testId="fundraiser-email-cancel-dialog"
        confirmTestId="fundraiser-email-cancel-confirm"
        busy={cancel.isPending}
        tone="danger"
        onConfirm={() => cancel.mutate()}
      />
    </FundraisersPageShell>
  );
}

function SummaryBox({ children, testId }: { children: ReactNode; testId: string }) {
  return (
    <div
      className="flex flex-col gap-6 rounded-lg bg-surface-sunken px-20 py-16"
      {...testIdProp(testId)}
    >
      {children}
    </div>
  );
}

function Note({ children, testId }: { children: ReactNode; testId?: string }) {
  return (
    <p className="text-s13 leading-h15 text-text-secondary" {...testIdProp(testId)}>
      {children}
    </p>
  );
}
